import type * as THREE from 'three'
import { resolveImportedModelTopologyRepository } from '../../topology/ImportedModelTopologyRepository'
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit'
import { ImportedModelInstancedTopologyDisplayEdges } from './ImportedModelInstancedTopologyDisplayEdges'

/**
 * 单个表面实例 Unit 的默认拓扑边线投影。
 *
 * 本对象验证全部 occurrence 的边线拓扑等价性，并只创建一个共享 instanceMatrix 的实例
 * LineSegments。FeatureResultMesh 继续承担拓扑语义，不再承担实例模型默认黑边。
 */
export class ImportedModelInstancedTopologyDisplayEdgeProjection {
  /** 已通过全部 occurrence 等价校验的种子拓扑快照。 */
  private readonly snapshots: readonly TopologySnapshot[] | null

  /** 当前活动的实例边线对象。 */
  private displayEdges: ImportedModelInstancedTopologyDisplayEdges | null = null

  /** 当前投影是否已经释放。 */
  private disposed = false
  /** 最新主题颜色供临时透明边线复用。 */
  private color: THREE.ColorRepresentation = 0x000000

  /**
   * 创建表面 Unit 的实例边线投影。
   *
   * @param slot 表面 Unit 使用的正式渲染槽。
   * @param surfaceUnit 持有共享 instanceMatrix 的表面 Unit。
   */
  public constructor(
    private readonly slot: PuppyCAD.ImportedModelInstancingRenderSlot,
    private readonly surfaceUnit: ImportedModelInstancedMeshUnit,
  ) {
    this.snapshots = this.resolveCompatibleTopologySnapshots()
  }

  /**
   * 按当前模型显示模式启停实例边线。
   *
   * @param enabled 是否显示默认拓扑边线。
   * @param color 当前边线颜色。
   * @returns 当前 Unit 具备等价拓扑并已接管边线职责时返回 true。
   */
  public setEnabled(enabled: boolean, color: THREE.ColorRepresentation): boolean {
    this.color = color
    if (this.disposed || !this.snapshots) {
      return false
    }
    if (!enabled) {
      this.displayEdges?.dispose()
      this.displayEdges = null
      return true
    }
    if (this.displayEdges) {
      this.displayEdges.setDisplayEdgeColor(color)
      return true
    }

    const surfaceObject = this.surfaceUnit.object
    this.displayEdges = new ImportedModelInstancedTopologyDisplayEdges(
      this.snapshots,
      color,
      surfaceObject.count,
      surfaceObject.instanceMatrix,
    )
    surfaceObject.add(this.displayEdges)
    return true
  }

  /** @returns 正式边线对象，仅借用，不转移资源所有权。 */
  public getObject(): ImportedModelInstancedTopologyDisplayEdges | null { return this.displayEdges }
  /** @returns 最新主题要求的边线颜色。 */
  public getColor(): THREE.ColorRepresentation { return this.color }

  /** @param matrix 单槽表面矩阵。 @returns 使用已校验拓扑的独占临时边线。 */
  public createTemporary(matrix: THREE.InstancedBufferAttribute): ImportedModelInstancedTopologyDisplayEdges | null {
    if (!this.displayEdges || !this.snapshots) return null
    return new ImportedModelInstancedTopologyDisplayEdges(this.snapshots, this.color, 1, matrix)
  }

  /** 释放当前实例边线投影。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.displayEdges?.dispose()
    this.displayEdges = null
  }

  /**
   * 解析并校验全部 occurrence 可共享的拓扑快照。
   *
   * @returns 等价时返回种子快照；任一成员缺少或不等价时返回 null 并保留普通边线降级。
   */
  private resolveCompatibleTopologySnapshots(): readonly TopologySnapshot[] | null {
    const seedSourceMesh = this.slot.members[0]?.sourceMesh
    if (!seedSourceMesh) {
      return null
    }
    const seedSnapshots = this.collectSourceMeshTopologySnapshots(seedSourceMesh)
    if (seedSnapshots.length === 0) {
      return null
    }
    const seedSignature = this.createSnapshotsSignature(seedSnapshots)
    const allMembersCompatible = this.slot.members.every((member) => {
      const candidateSnapshots = this.collectSourceMeshTopologySnapshots(member.sourceMesh)
      return candidateSnapshots.length > 0
        && this.createSnapshotsSignature(candidateSnapshots) === seedSignature
    })
    return allMembersCompatible ? Object.freeze(seedSnapshots) : null
  }

  /**
   * 收集一个 source Mesh 的去重拓扑快照。
   *
   * @param sourceMesh 当前 occurrence 的显示源 Mesh。
   * @returns 按边线签名稳定排序的拓扑快照。
   */
  private collectSourceMeshTopologySnapshots(sourceMesh: THREE.Mesh): TopologySnapshot[] {
    const snapshotsBySignature = new Map<string, TopologySnapshot>()
    resolveImportedModelTopologyRepository(sourceMesh)
      ?.resolveBodyRecordsByVisibleMesh(sourceMesh)
      .forEach((bodyRecord) => {
        const snapshot = bodyRecord.topologyAsset.meshData.topologySnapshot
        if (!snapshot) {
          return
        }
        snapshotsBySignature.set(this.createSnapshotSignature(snapshot), snapshot)
      })
    return [...snapshotsBySignature.entries()]
      .sort(([leftSignature], [rightSignature]) => leftSignature.localeCompare(rightSignature))
      .map(([, snapshot]) => snapshot)
  }

  /**
   * 创建多个拓扑快照的稳定边线签名。
   *
   * @param snapshots 待比较快照。
   * @returns 保持快照顺序的稳定签名。
   */
  private createSnapshotsSignature(snapshots: readonly TopologySnapshot[]): string {
    return snapshots.map(snapshot => this.createSnapshotSignature(snapshot)).join('\u0000')
  }

  /**
   * 创建单个拓扑快照的边线几何签名。
   *
   * 只纳入边线实际消费的端点和采样坐标，面属性变化不会无谓拆分边线批次。
   *
   * @param snapshot 待签名拓扑快照。
   * @returns 可精确比较边线几何的稳定字符串。
   */
  private createSnapshotSignature(snapshot: TopologySnapshot): string {
    return JSON.stringify({
      vertices: snapshot.vertices.map(vertex => [vertex.index, ...vertex.position]),
      edges: snapshot.edges.map(edge => [
        edge.index,
        edge.vertexAIndex,
        edge.vertexBIndex,
        edge.sampledPoints ?? null,
      ]),
    })
  }
}
